import React, { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Select, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useCampfireTheme } from '../../../../../../../theme/useCampfireTheme';
import { PwiQuestion, PwiResults, PwiStage } from './pwi-results';

const useStyles = makeStyles(() =>
  createStyles({
    tableWrap: {
      overflowX: 'auto',
      border: '1px solid #EFEFEF',
      borderRadius: 6,
    },
    table: {
      borderCollapse: 'collapse',
      width: '100%',
      fontSize: '13px',
      fontVariantNumeric: 'tabular-nums',
      '& th, & td': {
        padding: '6px 10px',
        borderBottom: '1px solid #F0F0F0',
        textAlign: 'right',
        whiteSpace: 'nowrap',
      },
      '& th': {
        background: '#FAFAFA',
        fontWeight: 600,
        color: '#646464',
        verticalAlign: 'bottom',
      },
    },
    questionLabel: {
      display: 'block',
      fontWeight: 400,
      fontSize: '11px',
      whiteSpace: 'normal',
      minWidth: 90,
      maxWidth: 140,
      color: '#808080',
    },
    labelCell: {
      textAlign: 'left !important' as 'left',
    },
    stageCell: {
      textAlign: 'left !important' as 'left',
      fontWeight: 600,
      color: '#343434',
      verticalAlign: 'top',
    },
    indexCell: {
      fontWeight: 600,
      background: '#FAFAFA',
    },
    summary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 12,
      marginBottom: 20,
    },
    summaryCard: {
      border: '1px solid #EFEFEF',
      borderRadius: 6,
      padding: '12px 14px',
    },
  })
);

const ALL = 'all';

const percent = (value: number | null) => (value === null ? '–' : `${Math.round(value)}%`);
const score = (value: number | null) => (value === null ? '–' : Math.round(value).toString());
const change = (value: number | null) => {
  if (value === null) return '–';
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

type Props = {
  open: boolean;
  onClose: () => void;
  name: string;
  results: PwiResults;
};

export function PwiExpandedDialog({ open, onClose, name, results }: Props) {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [scope, setScope] = useState<string>(ALL);

  const selected = results.subAccounts.find((subAccount) => subAccount.name === scope);
  const stages: PwiStage[] = selected ? selected.stages : results.stages;
  const questions: PwiQuestion[] = selected ? selected.questions : results.questions;
  const improvement = selected ? selected.improvement : results.improvement;

  const bandColour = (band: PwiStage['band']) => {
    if (band === 'normal') return theme.alert.green.dark;
    if (band === 'challenged') return theme.alert.yellow.dark;
    if (band === 'high-risk') return theme.alert.red.dark;
    return '#808080';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle style={{ flexShrink: 0 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between' style={{ gap: 12 }}>
          <Box>
            <Box style={{ fontSize: '20px', fontWeight: 500 }}>{name}</Box>
            <Box style={{ fontSize: '14px', color: theme.color.grey.neutral300 }}>Personal Wellbeing Index</Box>
          </Box>
          <Box display='flex' alignItems='center' style={{ gap: 12 }}>
            {results.subAccounts.length > 0 && (
              <Select
                value={scope}
                onChange={(event) => setScope(event.target.value as string)}
                variant='outlined'
                style={{ height: 36, fontSize: '13px', minWidth: 200 }}
              >
                <MenuItem value={ALL}>All sub-accounts</MenuItem>
                {results.subAccounts.map((subAccount) => (
                  <MenuItem key={subAccount.name} value={subAccount.name}>
                    {subAccount.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            <IconButton onClick={onClose} size='small' aria-label='Close'>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px' }}>
        <Box className={classes.summary}>
          {stages.map((stage) => (
            <Box key={stage.stage} className={classes.summaryCard}>
              <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#646464' }}>{stage.label}</Typography>
              <Typography style={{ fontSize: '28px', fontWeight: 800, color: '#343434' }}>
                {score(stage.index)}
              </Typography>
              <Typography style={{ fontSize: '12px', fontWeight: 600, color: bandColour(stage.band) }}>
                {stage.band === 'normal' && 'Normal'}
                {stage.band === 'challenged' && 'Challenged'}
                {stage.band === 'high-risk' && 'High-risk'}
                {stage.band === null && 'No surveys'}
              </Typography>
              <Typography style={{ fontSize: '12px', color: '#808080' }}>{`${stage.surveys} surveys`}</Typography>
            </Box>
          ))}
          <Box className={classes.summaryCard}>
            <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#646464' }}>Improvement</Typography>
            <Typography style={{ fontSize: '13px', marginTop: 6 }}>{`Start → 6M ${change(
              improvement.sixMonth
            )}`}</Typography>
            <Typography style={{ fontSize: '13px' }}>{`6M → Closure ${change(improvement.closure)}`}</Typography>
            <Typography style={{ fontSize: '13px' }}>{`Start → Closure ${change(improvement.overall)}`}</Typography>
          </Box>
        </Box>

        <Typography style={{ fontSize: '13px', fontWeight: 600, color: '#646464', marginBottom: 8 }}>
          Data for all participants
        </Typography>
        <Box className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.labelCell}>Survey</th>
                <th className={classes.labelCell}>Totals</th>
                {questions.map((question, position) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <th key={position}>
                    {`Q${position + 1}`}
                    <span className={classes.questionLabel}>
                      {question.label}
                      {question.role === 'separate' ? ' (separate)' : ''}
                    </span>
                  </th>
                ))}
                <th>Index</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((stage, stageIndex) => (
                <React.Fragment key={stage.stage}>
                  <tr>
                    <td rowSpan={5} className={classes.stageCell}>
                      {stage.label}
                    </td>
                    <td className={classes.labelCell}>Total score</td>
                    {questions.map((question, position) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={position}>
                        {question.stages[stageIndex].answers ? question.stages[stageIndex].total : '–'}
                      </td>
                    ))}
                    <td className={classes.indexCell}>NA</td>
                  </tr>
                  <tr>
                    <td className={classes.labelCell}>No. of answers</td>
                    {questions.map((question, position) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={position}>{question.stages[stageIndex].answers}</td>
                    ))}
                    <td className={classes.indexCell}>{stage.surveys}</td>
                  </tr>
                  <tr>
                    <td className={classes.labelCell}>Average</td>
                    {questions.map((question, position) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={position}>{percent(question.stages[stageIndex].average)}</td>
                    ))}
                    <td className={classes.indexCell}>{percent(stage.index)}</td>
                  </tr>
                  <tr>
                    <td className={classes.labelCell}>Min</td>
                    {questions.map((question, position) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={position}>{question.stages[stageIndex].min ?? '–'}</td>
                    ))}
                    <td className={classes.indexCell} />
                  </tr>
                  <tr>
                    <td className={classes.labelCell}>Max</td>
                    {questions.map((question, position) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={position}>{question.stages[stageIndex].max ?? '–'}</td>
                    ))}
                    <td className={classes.indexCell} />
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Box>
        <Typography style={{ fontSize: '12px', color: '#808080', marginTop: 10 }}>
          Totals and min/max use each question&apos;s own scale. Averages and the index are shown out of 100. Bands: 70+
          Normal, 51-69 Challenged, 50 or under High-risk.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
